import React from "react";
import { Chip } from "@mui/material";
import type { GroupCoverage, UserCoverage } from "src/api/shares";

export type CoverageState = "full" | "partial" | "none";

export const userCoverageState = (
  row: UserCoverage,
  datasetsTotal: number,
): CoverageState =>
  datasetsTotal === 0 || row.readable_count === datasetsTotal
    ? "full"
    : row.readable_count === 0
      ? "none"
      : "partial";

export const groupHasGaps = (row: GroupCoverage): boolean =>
  row.members_with_gaps > 0;

const COLOR: Record<CoverageState, "success" | "warning" | "error"> = {
  full: "success",
  partial: "warning",
  none: "error",
};

interface CoverageChipProps {
  label: string;
  state: CoverageState;
  expanded: boolean;
  onToggle: () => void;
  tid: string;
}

/** A recipient's dataset coverage at a glance; click expands the detail. */
const CoverageChip: React.FC<CoverageChipProps> = ({
  label,
  state,
  expanded,
  onToggle,
  tid,
}) => (
  <Chip
    size="small"
    label={label}
    color={COLOR[state]}
    variant={expanded ? "filled" : "outlined"}
    onClick={onToggle}
    data-testid={tid}
    data-coverage={state}
    aria-expanded={expanded}
  />
);

export const userChipLabel = (row: UserCoverage, datasetsTotal: number) => {
  const state = userCoverageState(row, datasetsTotal);
  if (state === "full") return "Full";
  if (state === "none") return "None";
  return `${row.readable_count} of ${datasetsTotal}`;
};

export const groupChipLabel = (row: GroupCoverage) =>
  row.members_evaluated > 0 && !groupHasGaps(row)
    ? "Full"
    : `${row.members_full} full · ${row.members_with_gaps} with gaps`;

export default CoverageChip;
